import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDraftState } from "../context/DraftStateContext";
import { useLeadGenerationJob, type LeadJobState } from "../hooks/useLeadGenerationJob";
import {
  apiPut,
  cancelJob,
  estimateLeadGeneration,
  generateLeadsBulk,
  getLeadSettings,
  planLeadResearchBulk,
  saveLeadModel,
} from "../lib/api";
import { shortModel, usd } from "../lib/leads";
import type { LeadGenerationEstimate, LeadSettings, OpenRouterModel } from "../lib/types";
import { formatApiError } from "../lib/utils";
import { Icon } from "./Icon";
import { useToast } from "./Toast";
import { Button, Select } from "./ui";

type PlannedTarget = { target_name: string; leads: number; queries: number };
type Unit = { one: string; many: string };

const PROJECT_UNIT: Unit = { one: "project", many: "projects" };

function plural(count: number, unit: Unit): string {
  return `${count} ${count === 1 ? unit.one : unit.many}`;
}

function modelOptions(pool: OpenRouterModel[], current: string): OpenRouterModel[] {
  const list = [...pool].sort((a, b) =>
    (a.name || a.id).localeCompare(b.name || b.id, undefined, { sensitivity: "base" }),
  );
  if (current && !list.some((m) => m.id === current)) {
    list.unshift({ id: current, name: current });
  }
  return list;
}

function finishedMessage(state: LeadJobState): { text: string; error: boolean } {
  const p = state.progress;
  if (state.status === "cancelled") return { text: "Lead generation stopped.", error: true };
  if (state.status !== "ok" || !p) {
    return { text: state.error || `Lead generation ended: ${state.status}`, error: true };
  }
  const parts = [`${p.leads} lead${p.leads === 1 ? "" : "s"} for ${plural(p.generated, PROJECT_UNIT)}`];
  if (p.unchanged) parts.push(`${p.unchanged} already current`);
  const viaLead = (p.results || []).filter((r) => r.reason === "found_via_lead").length;
  if (viaLead) parts.push(`${viaLead} found via lead research (no second round)`);
  if (p.skipped - viaLead > 0) parts.push(`${p.skipped - viaLead} skipped`);
  if (p.failed) parts.push(`${p.failed} failed`);
  parts.push(usd(p.cost_usd, 3));
  let text = `Leads generated: ${parts.join(" · ")}.`;
  if (p.planned?.error) text += ` Sending to Search failed: ${p.planned.error}`;
  else if (p.planned) {
    text += ` Sent ${p.planned.planned ?? 0} lead${p.planned.planned === 1 ? "" : "s"} to Search (${p.planned.queries ?? 0} queries).`;
  }
  return { text, error: p.failed > 0 && p.generated === 0 };
}

/**
 * Bulk lead generation and "send leads to Search" for a selection of projects,
 * whether they were picked one by one (Projects) or as whole vendor clusters.
 *
 * Generation is priced before it starts (per bank, with each ledger's remaining
 * budget) and runs as one background job with a live progress line.
 */
export function LeadBulkToolbar({
  projectIds,
  selectionCount,
  unit = PROJECT_UNIT,
  visibleCount,
  allVisibleSelected,
  onToggleAll,
  selectAllLabel,
  sendToSearchDefault = false,
  storageKey = "orion1.leads.bulkRun",
  onChanged,
}: {
  /** The projects every action applies to. */
  projectIds: string[];
  /** How many units (projects or clusters) are selected, for the labels. */
  selectionCount: number;
  unit?: Unit;
  visibleCount: number;
  allVisibleSelected: boolean;
  onToggleAll: (select: boolean) => void;
  selectAllLabel?: string;
  /** Tick "then send leads to Search" by default. */
  sendToSearchDefault?: boolean;
  /** Remembers a running job per page so it resumes after navigation. */
  storageKey?: string;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { setDraft } = useDraftState();
  const [settings, setSettings] = useState<LeadSettings | null>(null);
  const [model, setModel] = useState("");
  const [estimate, setEstimate] = useState<LeadGenerationEstimate | null>(null);
  // The projects the shown estimate was priced for; Start runs exactly these.
  const [estimateIds, setEstimateIds] = useState<string[]>([]);
  const [force, setForce] = useState(false);
  const [sendToSearch, setSendToSearch] = useState(sendToSearchDefault);
  const [busy, setBusy] = useState(false);
  const [planned, setPlanned] = useState<PlannedTarget[]>([]);
  const [failures, setFailures] = useState<{ name: string; error: string }[]>([]);

  const job = useLeadGenerationJob(storageKey, (state) => {
    const message = finishedMessage(state);
    toast(message.text, message.error);
    setFailures(
      (state.progress?.results || [])
        .filter((r) => r.status === "failed" || r.reason === "no_spend_ledger" || r.reason === "run_call_cap")
        .map((r) => ({
          name: r.project_name || r.project_id,
          error:
            r.error ||
            (r.reason === "no_spend_ledger"
              ? "No spend ledger for this bank yet: run Phase 1 for it once"
              : r.reason === "run_call_cap"
                ? "Call cap for one run reached: generate again for the rest"
                : r.reason || r.status),
        })),
    );
    const byTarget = state.progress?.planned?.by_target || [];
    if (byTarget.length) setPlanned(byTarget);
    onChanged();
  });

  useEffect(() => {
    getLeadSettings()
      .then((data) => {
        setSettings(data);
        setModel(data.model);
      })
      .catch(() => setSettings(null));
  }, []);

  const options = useMemo(() => modelOptions(settings?.models || [], model), [settings, model]);
  const selectionKey = projectIds.join(",");
  const disabled = busy || job.running || !settings?.enabled;
  const projectCount = projectIds.length;
  const scopeText =
    unit.one === PROJECT_UNIT.one
      ? `${selectionCount}`
      : `${plural(selectionCount, unit)} · ${plural(projectCount, PROJECT_UNIT)}`;

  // A different selection is a different price: drop the old confirmation.
  useEffect(() => {
    setEstimate(null);
  }, [selectionKey]);

  const changeModel = async (next: string) => {
    setModel(next);
    setEstimate(null);
    try {
      const saved = await saveLeadModel(next);
      setSettings(saved);
    } catch (e) {
      toast(formatApiError(e), true);
    }
  };

  const loadEstimate = useCallback(
    async (nextForce: boolean) => {
      setBusy(true);
      try {
        const ids = [...projectIds];
        const next = await estimateLeadGeneration({ project_ids: ids, model, force: nextForce });
        setEstimateIds(ids);
        setEstimate(next);
      } catch (e) {
        toast(formatApiError(e), true);
      } finally {
        setBusy(false);
      }
    },
    [model, projectIds, toast],
  );

  const startGeneration = async () => {
    setBusy(true);
    setFailures([]);
    setPlanned([]);
    try {
      const accepted = await generateLeadsBulk({
        project_ids: estimateIds,
        model,
        force,
        send_to_search: sendToSearch,
      });
      setEstimate(null);
      job.start(accepted.run_id);
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  const sendLeadsToSearch = async () => {
    setBusy(true);
    try {
      const result = await planLeadResearchBulk({ project_ids: projectIds });
      setPlanned(result.by_target);
      toast(
        result.planned
          ? `Sent ${result.planned} lead${result.planned === 1 ? "" : "s"} to Search: ${result.queries} ${result.queries === 1 ? "query" : "queries"}.`
          : `No open leads on the selected ${unit.many}. Generate leads first.`,
        !result.planned,
      );
      onChanged();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  const openSearch = async (targetName: string) => {
    try {
      await apiPut("/api/workspace", { target_company: targetName });
    } catch {
      /* the Search page can still switch banks itself */
    }
    setDraft({ target_company: targetName });
    navigate("/search?leads=1");
  };

  const progress = job.state?.progress;
  const noLedger = (estimate?.by_target || []).filter((t) => !t.has_ledger && t.to_generate > 0);

  return (
    <div className="px-6 pb-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-body-sm text-on-surface-variant">
          <input
            type="checkbox"
            checked={allVisibleSelected && visibleCount > 0}
            disabled={!visibleCount}
            onChange={(e) => onToggleAll(e.target.checked)}
          />
          {selectAllLabel || `Select all shown (${visibleCount})`}
        </label>
        <span className="text-body-sm font-data-mono text-on-surface-variant">
          {unit.one === PROJECT_UNIT.one
            ? `${selectionCount} selected`
            : `${plural(selectionCount, unit)} selected · ${plural(projectCount, PROJECT_UNIT)}`}
        </span>
        <div className="flex items-center gap-2 min-w-[16rem]">
          <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label shrink-0">
            Lead model
          </span>
          <Select
            value={model}
            disabled={!settings || job.running}
            onChange={(e) => void changeModel(e.target.value)}
            aria-label="Model used to generate leads"
            className="py-1"
          >
            {options.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id === settings?.default_model ? `${m.name || m.id} (default)` : m.name || m.id}
              </option>
            ))}
          </Select>
        </div>
        <Button
          variant="primary"
          size="sm"
          disabled={disabled || !projectCount}
          onClick={() => void loadEstimate(force)}
          title={
            settings && !settings.enabled
              ? "ORION1_OPPORTUNITY_HYPOTHESES is off"
              : `${settings?.leads_per_event ?? 3} possible next projects per roadmap event, or 3 from the project record`
          }
        >
          <Icon name="auto_awesome" className="text-base" />
          Generate leads ({scopeText})
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled || !projectCount}
          onClick={() => void sendLeadsToSearch()}
          title="Plan every open lead's keywords as Phase 1 queries for its bank. Nothing runs until you run Search."
        >
          <Icon name="travel_explore" className="text-base" />
          Send leads to Search
        </Button>
      </div>

      {estimate ? (
        <div className="border border-primary/30 bg-primary/5 px-3 py-2 text-body-sm space-y-1">
          <p className="text-on-surface">
            {estimate.to_generate
              ? `${plural(estimate.to_generate, PROJECT_UNIT)} · ${estimate.calls_this_run} model call${estimate.calls_this_run === 1 ? "" : "s"} with ${shortModel(estimate.model)} · estimated ${usd(estimate.estimate_usd)}`
              : "Every selected project already has current leads."}
            {estimate.unchanged ? ` · ${estimate.unchanged} already current, skipped` : ""}
            {estimate.found_via_lead
              ? ` · ${estimate.found_via_lead} found via lead research, skipped (no second round)`
              : ""}
            {estimate.calls > estimate.call_cap
              ? ` · capped at ${estimate.call_cap} calls this run; generate again for the rest`
              : ""}
          </p>
          {estimate.by_target.length > 1 || noLedger.length ? (
            <ul className="text-[11px] font-data-mono text-on-surface-variant">
              {estimate.by_target.map((t) => (
                <li key={t.target_company_id}>
                  {t.target_name}: {t.to_generate} to generate · {usd(t.estimate_usd)}
                  {t.remaining_usd != null ? ` · ${usd(t.remaining_usd)} budget left` : ""}
                  {!t.has_ledger && t.to_generate ? " · no spend ledger yet, will be skipped" : ""}
                </li>
              ))}
            </ul>
          ) : null}
          <p className="text-[11px] text-on-surface-variant">
            Charged to each bank&apos;s budget. The estimate is{" "}
            {estimate.estimate_source === "ledger_average"
              ? "the average of earlier calls"
              : "catalog pricing until three calls have been billed"}
            .
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <label className="flex items-center gap-2 text-body-sm text-on-surface-variant">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => {
                  setForce(e.target.checked);
                  void loadEstimate(e.target.checked);
                }}
              />
              Regenerate projects that already have current leads
            </label>
            <label
              className="flex items-center gap-2 text-body-sm text-on-surface-variant"
              title="When generation finishes, plan every open lead's keywords as Phase 1 queries (no search runs yet)"
            >
              <input type="checkbox" checked={sendToSearch} onChange={(e) => setSendToSearch(e.target.checked)} />
              Then send all their leads to Search
            </label>
            <Button
              variant="primary"
              size="sm"
              disabled={busy || (!estimate.to_generate && !sendToSearch)}
              onClick={() => {
                if (estimate.to_generate) {
                  void startGeneration();
                } else {
                  // Nothing to generate: planning alone needs no job and no model.
                  setEstimate(null);
                  void sendLeadsToSearch();
                }
              }}
            >
              {estimate.to_generate ? "Start" : "Send current leads to Search"}
            </Button>
            <Button variant="link-muted" onClick={() => setEstimate(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {job.running ? (
        <div className="flex flex-wrap items-center gap-3 border border-outline-variant bg-surface-container px-3 py-2 text-body-sm">
          <span className="orion-spinner" aria-hidden />
          <span className="font-data-mono text-on-surface">
            {progress?.planning
              ? "Sending leads to Search…"
              : `Generating leads · ${progress?.done ?? 0}/${progress?.total ?? "…"} projects · ${progress?.leads ?? 0} leads${progress ? ` · ${usd(progress.cost_usd, 3)}` : ""}`}
          </span>
          <Button
            variant="link-muted"
            onClick={() => {
              if (job.state?.runId) {
                cancelJob(job.state.runId).catch((e) => toast(formatApiError(e), true));
              }
            }}
          >
            Stop
          </Button>
        </div>
      ) : null}

      {failures.length ? (
        <details className="border border-error/40 bg-error/5 px-3 py-2 text-body-sm">
          <summary className="cursor-pointer text-error">
            {plural(failures.length, PROJECT_UNIT)} without new leads
          </summary>
          <ul className="mt-1 space-y-0.5 text-[11px] font-data-mono text-on-surface-variant">
            {failures.map((f, index) => (
              <li key={`${f.name}-${index}`}>
                {f.name}: {f.error}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {planned.length ? (
        <div className="flex flex-wrap items-center gap-2 text-body-sm">
          <span className="text-on-surface-variant">See the lead queries in Search:</span>
          {planned.map((t) => (
            <Button key={t.target_name} variant="outline-primary" size="sm" onClick={() => void openSearch(t.target_name)}>
              {t.target_name} · {t.queries} {t.queries === 1 ? "query" : "queries"}
            </Button>
          ))}
          <Button variant="link-muted" onClick={() => setPlanned([])}>
            Hide
          </Button>
        </div>
      ) : null}
    </div>
  );
}
